import { Capacitor } from '@capacitor/core';
import { NativePurchases } from '@capgo/native-purchases';

export const IAP_PRODUCT_IDS = {
  basic_monthly: '12',
  basic_yearly: '1212',
  pro_monthly: '13',
  pro_yearly: '1313',
};

export interface IAPProduct {
  productId: string;
  title: string;
  description: string;
  price: string;
  priceValue: number;
  currency: string;
}

class IAPService {
  async getProducts(productIds: string[]): Promise<IAPProduct[]> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return [];
    }

    try {
      const result = await NativePurchases.getProducts({ productIdentifiers: productIds });
      return result.products.map((product: any) => ({
        productId: product.identifier,
        title: product.localizedTitle || product.identifier,
        description: product.localizedDescription || '',
        price: product.price,
        priceValue: parseFloat(product.price.replace(/[^0-9,.]/g, '').replace(',', '.')),
        currency: product.currencyCode || 'BRL',
      }));
    } catch (error) {
      throw new Error('Falha ao carregar produtos da App Store.');
    }
  }

  async purchase(productId: string): Promise<any> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      throw new Error('IAP disponível apenas no iOS');
    }

    try {
      const result = await NativePurchases.purchaseProduct({ productIdentifier: productId });

      return {
        transactionId: result.transactionId,
        productId: result.productIdentifier,
        jwsRepresentation: result.jwsRepresentation || null,
        receipt: result.receipt || null,
      };
    } catch (error: any) {
      if (error.message?.includes('cancelada') || error.message?.includes('cancel') || error.message?.includes('cancelled')) {
        throw new Error('Compra cancelada pelo usuário');
      }
      throw error;
    }
  }

  async restorePurchases(): Promise<any> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      throw new Error('IAP disponível apenas no iOS');
    }

    try {
      await NativePurchases.restorePurchases();
      return { restored: true, productIds: [] };
    } catch (error) {
      throw error;
    }
  }

  async validateAndActivate(userId: string, transactionId: string, productId: string): Promise<void> {
    const API_URL = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
    const API_KEY = import.meta.env.VITE_API_SECRET_KEY;

    const response = await fetch(`${API_URL}/iap/validate-ios`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({ userId, transactionId, productId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Falha ao ativar assinatura');
    }
  }
}

export const iapService = new IAPService();
